<template>
  <div class="flex w-full gap-8 p-6">
    <div class="w-1/4 space-y-1">
      <h3 class="font-semibold text-dark-neutral-500">
        IMDRF MDCE 2019 appraisal Criteria
      </h3>
    </div>
    <div class="w-full">
      <SectionCard
        class="w-2/3"
        title="IMDRF MDCE 2019 appraisal Criteria"
        subtitle="IMDRF criteria for assessing clinical evidence, focusing on relevance, reliability, and robustness of data. Use to evaluate the quality of studies supporting device safety and performance."
        :display-content="
          review.entity.value?.plan?.appraisalPlan.isImdrfMdce2019Applicable
        "
      >
        <template #header-button>
          <PlanLockGuard>
            <Switch
              :disabled="review.isPlanReadonly.value"
              :model-value="
                review.entity.value?.plan?.appraisalPlan
                  .isImdrfMdce2019Applicable
              "
              @update:model-value="toggleAppraisalImdrfMdce2019"
            >
              <template #icon>
                <component
                  :is="
                    review.entity.value?.plan?.appraisalPlan
                      .isImdrfMdce2019Applicable
                      ? ArrowsPointingInIcon
                      : ArrowsPointingOutIcon
                  "
                  class="w-3 h-auto"
                  :class="
                    review.entity.value?.plan?.appraisalPlan
                      .isImdrfMdce2019Applicable
                      ? 'text-green-500'
                      : 'text-dark-neutral-100'
                  "
                />
              </template>
            </Switch>
          </PlanLockGuard>
        </template>
        <div
          v-if="
            review.entity.value?.plan?.appraisalPlan.isImdrfMdce2019Applicable
          "
          class="space-y-6"
        >
          <div
            v-for="criterion in reviewPlanAppraisalCriteria"
            :key="criterion.id"
            class="space-y-0"
          >
            <div class="pb-4 space-y-2">
              <p class="text-sm font-semibold text-gray-700">
                {{ criterion.id }} - Title: {{ criterion.title }}
              </p>
              <p class="text-sm text-slate-500 font-[400]">
                <span class="">Question:</span>
                {{ criterion.question }}
              </p>
            </div>
            <div class="flex flex-wrap gap-2 items-center text-primary">
              <div
                v-for="(answer, index) in criterion.answers"
                :key="answer.id"
                class="flex items-center text-sm gap-1 p-2 rounded-full text-blue-800 bg-blue-200 font-semibold leading-none"
              >
                <div class="flex items-center gap-1">
                  <div
                    class="rounded-3xl bg-white/40 font-normal text-sm px-2 py-1"
                  >
                    {{ criterion.id + (index + 1) }}
                  </div>
                  {{ answer.title }}
                </div>
                <PlanLockGuard>
                  <button
                    :disabled="review.isPlanReadonly.value"
                    @click="deleteAnswer(answer.id, criterion.id)"
                  >
                    <XMarkIcon class="w-4 text-primary" />
                  </button>
                </PlanLockGuard>
              </div>
              <PlanLockGuard>
                <FloatingTextInput
                  :disabled="review.isPlanReadonly.value"
                  @save="
                    addAnswer({
                      appraisalCriterionId: criterion.id,
                      answer: $event,
                    })
                  "
                >
                  <template #activator="{ show }">
                    <button
                      key=""
                      class="flex items-center gap-1 text-sm border-2 p-2 px-3 rounded-full text-slate-500 border-slate-300 border-dashed font-semibold"
                      :disabled="review.isPlanReadonly.value"
                      @click.stop="show"
                    >
                      <PlusIcon class="w-4 h-4" />Add answer
                    </button>
                  </template>
                </FloatingTextInput>
              </PlanLockGuard>
            </div>
          </div>
        </div>
      </SectionCard>
    </div>
  </div>
</template>

<script setup lang="ts">
import { injectStrict } from '@app/utils/injectStrict'
import { ReviewKey } from '@app/views/Review/use-review'
import { SnackbarState } from '@app/types'
import useSnackbar from '@app/composables/use-snackbar'
import useLoading from '@app/composables/use-loading'
import { HttpException } from '@core/exceptions/http.exception'
import FloatingTextInput from '@app/components/Global/Inputs/FloatingTextInput.vue'
import { ref, watchEffect } from 'vue'
import { AppraisalCriterion } from '@core/domain/models/appraisal-criterion.model'
import Switch from '@app/components/Global/Switch.vue'
import XMarkIcon from '@app/components/Icons/XMarkIcon.vue'
import PlusIcon from '@app/components/Icons/PlusIcon.vue'
import SectionCard from '@app/components/Global/SectionCard.vue'
import {
  ArrowsPointingOutIcon,
  ArrowsPointingInIcon,
} from '@heroicons/vue/24/outline'
import PlanLockGuard from '../PlanLockGuard.vue'

const snackbar = useSnackbar()
const loading = useLoading()

const review = injectStrict(ReviewKey)

const reviewPlanAppraisalCriteria = ref<AppraisalCriterion[]>([])

watchEffect(() => {
  reviewPlanAppraisalCriteria.value =
    review.entity.value.plan?.appraisalCriteria ?? []
})

async function addAnswer(data: {
  appraisalCriterionId: string
  answer: string
}) {
  loading.start()
  try {
    await review.addAppraisalCriterionAnswer(data)
    snackbar.success('Success')
  } catch (e) {
    const error = e as HttpException
    if (
      error.response.data.statusCode >= 400 &&
      error.response.data.statusCode < 500
    ) {
      snackbar.show(SnackbarState.ERROR, error.response.data.message)
    } else {
      snackbar.show(
        SnackbarState.ERROR,
        'An error occured while adding answer, please try again.',
      )
    }
    throw e
  } finally {
    loading.stop()
  }
}

async function deleteAnswer(answerId: string, appraisalCriterionId: string) {
  loading.start()
  try {
    await review.deleteAppraisalCriterionAnswer({
      answerId: answerId,
      appraisalCriterionId: appraisalCriterionId,
    })
    snackbar.success('Success')
  } catch (e) {
    const error = e as HttpException
    if (
      error.response.data.statusCode >= 400 &&
      error.response.data.statusCode < 500
    ) {
      snackbar.show(SnackbarState.ERROR, error.response.data.message)
    } else {
      snackbar.show(
        SnackbarState.ERROR,
        'An error occured while deleting answer, please try again.',
      )
    }
    throw e
  } finally {
    loading.stop()
  }
}

async function toggleAppraisalImdrfMdce2019() {
  loading.start()
  try {
    if (review.entity.value?.plan?.appraisalPlan.isImdrfMdce2019Applicable) {
      await review.disableStudiesAppraisal()
      snackbar.success('Success')
    } else {
      await review.enableStudiesAppraisalImdrfMdce2019()
      snackbar.success('Success')
    }
  } catch (e) {
    const error = e as HttpException
    if (
      error.response.data.statusCode >= 400 &&
      error.response.data.statusCode < 500
    ) {
      snackbar.show(SnackbarState.ERROR, error.response.data.message)
    } else {
      snackbar.show(
        SnackbarState.ERROR,
        'An error occured while enabling/disabling appraisal IMDRF MDCE 2019, please try again.',
      )
    }
    throw e
  } finally {
    loading.stop()
  }
}
</script>
